import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { map, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { ComponentCardComponent } from '../../../shared/components/common/component-card/component-card.component';
import { ButtonComponent } from '../../../shared/components/ui/button/button.component';
import { LeadCycleApiService } from '../services/lead-cycle-api.service';
import { LeadCycleRequest, LeadCycleResponse } from '../models/lead-cycle.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-lead-cycle-form',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    PageBreadcrumbComponent,
    ComponentCardComponent,
    ButtonComponent
  ],
  templateUrl: './lead-cycle-form.component.html'
})
export class LeadCycleFormComponent implements OnInit {
  loading = false;
  saving = false;
  error = '';
  isEditing = false;
  cycleId: number | null = null;

  checkingDuplicate = false;
  duplicateNameError = '';
  nameValidationError = '';
  descriptionValidationError = '';

  readonly NAME_MAX = 30;

  // Form model. displayOrder no se edita aquí: el orden se define arrastrando en la lista.
  name = '';
  private originalName = '';
  description = '';
  triggerAction: 'NONE' | 'CREATE_DEAL' | 'CLOSE_LEAD' = 'NONE';
  isActive = true;

  constructor(
    private cycleApi: LeadCycleApiService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private toast: ToastService
  ) { }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEditing = true;
      this.cycleId = +id;
      this.cargarCiclo(this.cycleId);
    }
  }

  private cargarCiclo(id: number): void {
    this.loading = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.cycleApi.getCycleById(id, businessId).subscribe({
      next: (ciclo) => {
        this.name = ciclo.name;
        this.originalName = ciclo.name;
        this.description = ciclo.description || '';
        this.triggerAction = ciclo.triggerAction;
        this.isActive = ciclo.isActive;
        this.loading = false;
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar el ciclo';
        this.loading = false;
      }
    });
  }

  onNameChange(value: string): void {
    let filtered = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '');
    if (filtered.length > this.NAME_MAX) {
      filtered = filtered.substring(0, this.NAME_MAX);
      this.nameValidationError = 'Máximo 30 caracteres';
    } else if (filtered !== value) {
      this.nameValidationError = 'Solo se permiten letras';
    } else {
      this.nameValidationError = '';
    }
    setTimeout(() => { this.name = filtered; });
  }

  onDescriptionChange(value: string): void {
    const filtered = value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ\s]/g, '');
    if (filtered !== value) {
      this.descriptionValidationError = 'Solo se permiten letras';
    } else {
      this.descriptionValidationError = '';
    }
    setTimeout(() => { this.description = filtered; });
  }

  get pageTitle(): string {
    return this.isEditing ? 'Editar Ciclo de Lead' : 'Nuevo Ciclo de Lead';
  }

  guardar(): void {
    if (!this.name.trim()) {
      this.error = 'El nombre es requerido';
      this.toast.warning({ title: 'Campos incompletos', message: this.error });
      return;
    }

    this.error = '';
    this.duplicateNameError = '';
    this.nameValidationError = '';
    this.descriptionValidationError = '';
    this.checkingDuplicate = true;

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.cycleApi.getCycles(businessId).pipe(
      map(data => data || []),
      catchError(() => of([] as LeadCycleResponse[]))
    ).subscribe(existingCycles => {
      this.checkingDuplicate = false;

      const normalizedName = this.name.trim().toLowerCase();
      const duplicateName = existingCycles.find(c =>
        c.name.trim().toLowerCase() === normalizedName &&
        (!this.isEditing || c.id !== this.cycleId)
      );

      if (duplicateName) {
        this.duplicateNameError = `Ya existe un ciclo con el nombre "${duplicateName.name}"`;
        return;
      }

      this.executeSave();
    });
  }

  private executeSave(): void {
    this.saving = true;
    this.error = '';

    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    // Sin displayOrder: al crear el backend lo pone al final; al editar conserva el actual.
    const request: LeadCycleRequest = {
      businessId,
      name: this.name.trim(),
      description: this.description.trim() || undefined,
      triggerAction: this.triggerAction,
      isActive: this.isActive
    };

    if (this.isEditing && this.cycleId) {
      this.cycleApi.updateCycle(this.cycleId, request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.updated('Ciclo de lead',
            this.originalName !== this.name.trim()
              ? `Se actualizó "${this.originalName}" → "${this.name.trim()}".`
              : `Se actualizó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-leads/ciclos']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al actualizar el ciclo';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    } else {
      this.cycleApi.createCycle(request).subscribe({
        next: () => {
          this.saving = false;
          this.toast.created('Ciclo de lead', `Se creó "${this.name.trim()}" correctamente.`);
          this.router.navigate(['/config-leads/ciclos']);
        },
        error: (err) => {
          this.saving = false;
          this.error = err.error?.message || 'Error al crear el ciclo';
          this.toast.error({ title: 'Error', message: this.error });
        }
      });
    }
  }

  cancelar(): void {
    this.router.navigate(['/config-leads/ciclos']);
  }
}
